/**
 * Add Hardware Modal
 * Form to add new hardware device
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { hardwareService } from '../../services/hardwareService';
import type { Manufacturer, PhoneModel, PhoneType, HardwareFormData } from './types';

interface AddHardwareModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  customerId?: number;
}

const AddHardwareModal: React.FC<AddHardwareModalProps> = ({ open, onClose, onSuccess, customerId }) => {
  const [formData, setFormData] = useState<HardwareFormData>({
    manufacturerId: 0,
    modelId: 0,
    typeId: 0,
    mac: '',
    serial: '',
    location: '',
  });

  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [models, setModels] = useState<PhoneModel[]>([]);
  const [filteredModels, setFilteredModels] = useState<PhoneModel[]>([]);
  const [types, setTypes] = useState<PhoneType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadLookupData();
    }
  }, [open]);

  useEffect(() => {
    // Filter models based on selected manufacturer
    if (formData.manufacturerId) {
      const manufacturer = manufacturers.find(m => m.manufacturerID === formData.manufacturerId);
      if (manufacturer) {
        setFilteredModels(models.filter(m => m.Manufacturer === manufacturer.Description));
      }
    } else {
      setFilteredModels(models);
    }
  }, [formData.manufacturerId, manufacturers, models]);

  const loadLookupData = async () => {
    try {
      const [mfgData, modelData, typeData] = await Promise.all([
        hardwareService.getManufacturers(),
        hardwareService.getModels(),
        hardwareService.getPhoneTypes(),
      ]);
      setManufacturers(mfgData);
      setModels(modelData);
      setTypes(typeData);
    } catch (err: any) {
      setError(err.message || 'Failed to load form data');
    }
  };

  const handleChange = (field: keyof HardwareFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear model when manufacturer changes
    if (field === 'manufacturerId') {
      setFormData(prev => ({ ...prev, modelId: 0 }));
    }
  };

  const validateMac = (mac: string): boolean => {
    // Remove common separators
    const cleaned = mac.replace(/[:\-\s]/g, '');
    // Check if 12 hex characters
    return /^[0-9A-Fa-f]{12}$/.test(cleaned);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.manufacturerId || !formData.modelId || !formData.mac) {
      setError('Please fill in all required fields');
      return;
    }

    if (!validateMac(formData.mac)) {
      setError('Invalid MAC address format. Must be 12 hexadecimal characters.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Clean MAC address (remove separators)
      const cleanedMac = formData.mac.replace(/[:\-\s]/g, '').toLowerCase();

      await hardwareService.create({
        ...formData,
        mac: cleanedMac,
      }, customerId);

      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to add hardware');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      manufacturerId: 0,
      modelId: 0,
      typeId: 0,
      mac: '',
      serial: '',
      location: '',
    });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Hardware</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              select
              label="Manufacturer"
              value={formData.manufacturerId}
              onChange={(e) => handleChange('manufacturerId', parseInt(e.target.value))}
            >
              <MenuItem value={0}>Select Manufacturer</MenuItem>
              {manufacturers.map((mfg) => (
                <MenuItem key={mfg.manufacturerID} value={mfg.manufacturerID}>
                  {mfg.Description}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              select
              label="Model"
              value={formData.modelId}
              onChange={(e) => handleChange('modelId', parseInt(e.target.value))}
              disabled={!formData.manufacturerId}
            >
              <MenuItem value={0}>Select Model</MenuItem>
              {filteredModels.map((model) => (
                <MenuItem key={model.ModelID} value={model.ModelID}>
                  {model.Description} ({model.Linekeys} line keys)
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Type"
              value={formData.typeId || 0}
              onChange={(e) => handleChange('typeId', parseInt(e.target.value))}
            >
              <MenuItem value={0}>Select Type (Optional)</MenuItem>
              {types.map((type) => (
                <MenuItem key={type.PhoneTypeID} value={type.PhoneTypeID}>
                  {type.Description}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="MAC Address"
              value={formData.mac}
              onChange={(e) => handleChange('mac', e.target.value)}
              placeholder="001122334455 or 00:11:22:33:44:55"
              helperText="12 hexadecimal characters (colons optional)"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Serial Number"
              value={formData.serial}
              onChange={(e) => handleChange('serial', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Location"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              placeholder="e.g., Main Office, Reception Desk"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.manufacturerId || !formData.modelId || !formData.mac}
        >
          {loading ? <CircularProgress size={24} /> : 'Add Hardware'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddHardwareModal;
